import React, { useState, useRef } from 'react';
import { PhoneCall, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { trackCallbackSubmit } from '../utils/analytics';
import { PRIMARY_PHONE, PRIMARY_PHONE_DISPLAY } from '../constants/stores';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** A szerviz-oldalak szolgáltatás-választója. A `value` megy az analytics
 *  `service_type` paraméterébe és az adminba – kódérték, nem szabadszöveg. */
export const SERVICE_OPTIONS = [
  { value: 'kijelzocsere', label: 'Kijelzőcsere' },
  { value: 'akkumulatorcsere', label: 'Akkumulátorcsere' },
  { value: 'hatlapcsere', label: 'Hátlapcsere' },
  { value: 'hidrogel_folia', label: 'Hidrogél fólia' },
  { value: 'felvasarlas', label: 'Készülék felvásárlás / beszámítás' },
  { value: 'egyeb', label: 'Egyéb / nem tudom' },
];

/**
 * CallbackForm (Sprint 2A / FELADAT 2)
 *
 * „Kérj visszahívást” blokk. Akkor is fogja a leadet, ha zárva vagyunk vagy a
 * látogató nem tud/akar telefonálni.
 *
 * @param defaultService  előre kiválasztott szolgáltatás (oldalanként)
 * @param title/subtitle  felülírható szövegek
 * @param compact         szűkebb elrendezés (aloldali beágyazáshoz)
 * @param noteLabel/notePlaceholder  a szabadszöveges mező címkéje/placeholdere
 *        (a felvásárlás-oldalon „Készülék típusa” – az érték UGYANABBA a
 *        note mezőbe megy a backendre, nincs új adatút)
 * @param onSuccess       sikeres beküldés után hívódik (pl. oldal-specifikus
 *        konverziós esemény) – a beépített mérés ettől független, megmarad
 * @param options         a „Miben segíthetünk?” választék felülírása
 *        (alap: SERVICE_OPTIONS). A `value` megy az adminba és az analyticsbe.
 * @param defaultPhone    előre kitöltött telefonszám (a /nyitva oldal az SMS
 *        linkjéből, `?tel=` paraméterből tölti – a hívó ne gépelje be újra)
 * @param sourcePage      a `source_page` felülírása. A Telegram-értesítés EZT
 *        írja ki („Oldal:”), tehát ide tehető állapot is (pl. „/nyitva (zárva)”).
 * @param successTitle/successText  a köszönő képernyő szövege (a /nyitva
 *        oldalon más nyitvatartási időben és zárás után)
 */
export default function CallbackForm({
  defaultService = 'egyeb',
  title = 'Kérj visszahívást',
  subtitle = 'Hagyd itt a számod, és visszahívunk – akkor is, ha most zárva vagyunk.',
  compact = false,
  className = '',
  noteLabel = 'Megjegyzés',
  notePlaceholder = 'Pl. iPhone 12, törött kijelző',
  onSuccess,
  options = SERVICE_OPTIONS,
  defaultPhone = '',
  sourcePage,
  successTitle = 'Megvan, köszönjük!',
  successText,
}) {
  const [form, setForm] = useState({ name: '', phone: defaultPhone, service: defaultService, note: '' });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState('idle');    // idle | loading | success | error
  const [serverError, setServerError] = useState('');
  const honeypotRef = useRef(null);

  const validate = () => {
    const e = {};
    const name = form.name.trim();
    const digits = form.phone.replace(/\D/g, '');
    if (name.length < 2) e.name = 'Kérlek add meg a neved.';
    if (name.length > 80) e.name = 'A név túl hosszú (max. 80 karakter).';
    if (digits.length < 6 || digits.length > 15) e.phone = 'Adj meg egy érvényes telefonszámot.';
    if (form.note.length > 500) e.note = 'A megjegyzés túl hosszú (max. 500 karakter).';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (state === 'loading') return;             // dupla-submit védelem
    if (!validate()) return;
    setState('loading');
    setServerError('');
    try {
      const res = await fetch(`${API}/callbacks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          service_type: form.service,
          note: form.note.trim() || undefined,
          source_page: sourcePage || window.location.pathname,
          website: honeypotRef.current?.value || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setState('error');
        setServerError(data.detail || 'Nem sikerült elküldeni. Kérlek hívj minket telefonon.');
        return;
      }
      setState('success');
      trackCallbackSubmit({ service_type: form.service, source_page: sourcePage || window.location.pathname });
      if (onSuccess) { try { onSuccess(); } catch { /* a mérés nem törhet appot */ } }
    } catch {
      setState('error');
      setServerError('Hálózati hiba. Kérlek próbáld újra, vagy hívj minket.');
    }
  };

  const field = (key) => ({
    value: form[key],
    onChange: (e) => {
      setForm((f) => ({ ...f, [key]: e.target.value }));
      if (errors[key]) setErrors((x) => ({ ...x, [key]: undefined }));
    },
    'aria-invalid': errors[key] ? 'true' : undefined,
    'aria-describedby': errors[key] ? `cb-err-${key}` : undefined,
    className: `w-full border rounded-xl px-4 py-3 text-base outline-none transition-colors bg-white ${
      errors[key] ? 'border-red-400 bg-red-50/50' : 'border-gray-200 focus:border-brand'
    }`,
  });

  const Err = ({ k }) => errors[k] ? (
    <p id={`cb-err-${k}`} role="alert" className="text-xs text-red-600 mt-1 flex items-center gap-1">
      <AlertCircle size={13} aria-hidden="true" /> {errors[k]}
    </p>
  ) : null;

  if (state === 'success') {
    return (
      <div
        className={`bg-[#EAF8EC] border border-brand/25 rounded-2xl p-6 text-center ${className}`}
        role="status"
        data-testid="callback-success"
      >
        <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 size={30} className="text-brand" />
        </div>
        <p className="font-bold text-ink mb-1">{successTitle}</p>
        <p className="text-sm text-gray-600">
          {successText || 'Hamarosan visszahívunk a megadott számon.'} Ha sürgős, hívj minket:{' '}
          <a href={`tel:${PRIMARY_PHONE}`} className="text-brand font-semibold hover:underline">
            {PRIMARY_PHONE_DISPLAY}
          </a>
        </p>
      </div>
    );
  }

  return (
    <div
      className={`bg-gray-50 border border-gray-100 rounded-2xl p-5 sm:p-6 ${className}`}
      data-testid="callback-form"
    >
      <div className="flex items-center gap-2 mb-1">
        <PhoneCall size={20} className="text-brand" aria-hidden="true" />
        <h3 className="font-bold text-ink text-lg">{title}</h3>
      </div>
      <p className="text-sm text-gray-600 mb-4">{subtitle}</p>

      <form onSubmit={submit} noValidate className={compact ? 'space-y-3' : 'space-y-4'}>
        <div className={compact ? '' : 'grid sm:grid-cols-2 gap-4'}>
          <div className={compact ? 'mb-3' : ''}>
            <label htmlFor="cb-name" className="block text-sm font-semibold text-ink mb-1.5">
              Neved <span className="text-red-600" aria-hidden="true">*</span>
            </label>
            <input id="cb-name" type="text" autoComplete="name" maxLength={80}
              placeholder="Pl. Kovács Béla" required {...field('name')} />
            <Err k="name" />
          </div>
          <div>
            <label htmlFor="cb-phone" className="block text-sm font-semibold text-ink mb-1.5">
              Telefonszámod <span className="text-red-600" aria-hidden="true">*</span>
            </label>
            <input id="cb-phone" type="tel" inputMode="tel" autoComplete="tel" maxLength={32}
              placeholder="+36 30 123 4567" required {...field('phone')} />
            <Err k="phone" />
          </div>
        </div>

        <div>
          <label htmlFor="cb-service" className="block text-sm font-semibold text-ink mb-1.5">
            Miben segíthetünk?
          </label>
          <select id="cb-service" {...field('service')}>
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="cb-note" className="block text-sm font-semibold text-ink mb-1.5">
            {noteLabel} <span className="text-gray-500 font-normal">(nem kötelező)</span>
          </label>
          <textarea id="cb-note" rows={2} maxLength={500}
            placeholder={notePlaceholder} {...field('note')} />
          <Err k="note" />
        </div>

        {/* Honeypot */}
        <input ref={honeypotRef} type="text" name="website" tabIndex={-1} autoComplete="off"
          aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />

        {serverError && (
          <div role="alert" className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
            <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="text-sm text-red-700">
              {serverError}
              <a href={`tel:${PRIMARY_PHONE}`} className="block mt-1 font-semibold underline">
                Hívás: {PRIMARY_PHONE_DISPLAY}
              </a>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={state === 'loading'}
          data-testid="callback-submit"
          className="w-full flex items-center justify-center gap-2 py-3.5 bg-brand text-white font-bold rounded-xl hover:bg-brand-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {state === 'loading'
            ? (<><Loader2 size={18} className="animate-spin" aria-hidden="true" /> Küldés…</>)
            : (<><PhoneCall size={18} aria-hidden="true" /> Kérem a visszahívást</>)}
        </button>

        <p className="text-[11px] leading-relaxed text-gray-500 text-center">
          A kérés elküldésével hozzájárulsz, hogy a megadott telefonszámon felhívjunk. Nem küldünk reklámot.
        </p>
      </form>
    </div>
  );
}
